import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, Edit, Star, Trash2, Tag, X, Mail, Inbox } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { AdminSmartQR } from '../components/smartqr/AdminSmartQR';
import { categories, offerTypes, getOfferType, getAreasForCity } from '../data/constants';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [editingDeal, setEditingDeal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => { if (!user || user.role !== 'admin') { navigate('/login'); return; } fetchData(); }, [user]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [dealsRes, statsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/api/admin/deals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDeals(dealsRes.data);
      setStats(statsRes.data);
      setContacts(contactsRes.data);
    } catch (e) { toast.error('Failed to load data'); }
    setLoading(false);
  };

  const approveDeal = async (id) => { const token = localStorage.getItem('token'); try { await axios.post(`${API}/api/admin/deals/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Approved!'); fetchData(); } catch (e) { toast.error('Failed'); } };
  const featureDeal = async (id, featured) => { const token = localStorage.getItem('token'); try { await axios.post(`${API}/api/admin/deals/${id}/feature?featured=${featured}`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success(featured ? 'Featured!' : 'Unfeatured'); fetchData(); } catch (e) { toast.error('Failed'); } };
  const deleteDeal = async (id) => { if (!window.confirm('Delete?')) return; const token = localStorage.getItem('token'); try { await axios.delete(`${API}/api/admin/deals/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted'); fetchData(); } catch (e) { toast.error('Failed'); } };
  
  const openEditModal = (deal) => {
    setEditingDeal(deal);
    setEditForm({
      title: deal.title || '',
      description: deal.description || '',
      price: deal.price || '',
      category: deal.category || '',
      area: deal.area || '',
      business_name: deal.business_name || '',
      phone: deal.phone || '',
      offer_type: deal.offer_type || 'Budget Deal',
      image_url: deal.image_url || '',
      maps_link: deal.maps_link || ''
    });
  };

  const saveEdit = async () => {
    const token = localStorage.getItem('token');
    try {
      const formData = { ...editForm, price: editForm.price === '' || editForm.price === 0 ? null : editForm.price };
      await axios.put(`${API}/api/admin/deals/${editingDeal.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deal updated!');
      setEditingDeal(null);
      fetchData();
    } catch (e) { toast.error('Failed to update'); }
  };

  const replyToContact = (contact) => {
    const subject = encodeURIComponent(`Re: Your message to JustOffs`);
    const body = encodeURIComponent(`Hi ${contact.name},\n\nThank you for reaching out to JustOffs.\n\n${replyMessage}\n\nBest regards,\nJustOffs Team\n\n---\nYour original message:\n${contact.message}`);
    window.open(`mailto:${contact.email}?subject=${subject}&body=${body}`, '_blank');
    setReplyingTo(null);
    setReplyMessage('');
    toast.success('Email client opened with your reply!');
  };

  const openReplyForm = (contact) => {
    setReplyingTo(contact);
    setReplyMessage('');
  };

  const filteredDeals = tab === 'pending' ? deals.filter(d => !d.approved) : tab === 'featured' ? deals.filter(d => d.featured) : tab === 'contacts' ? [] : deals.filter(d => d.approved);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-4 border-slate-700 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-xl font-bold text-slate-800 mb-5">Admin Dashboard</h1>
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-white rounded-xl p-3 shadow-sm"><p className="text-slate-500 text-xs">Total</p><p className="text-xl font-bold text-slate-800" data-testid="stat-total">{stats.total_deals}</p></div>
            <div className="bg-white rounded-xl p-3 shadow-sm"><p className="text-slate-500 text-xs">Approved</p><p className="text-xl font-bold text-green-600" data-testid="stat-approved">{stats.approved_deals}</p></div>
            <div className="bg-white rounded-xl p-3 shadow-sm"><p className="text-slate-500 text-xs">Pending</p><p className="text-xl font-bold text-amber-500" data-testid="stat-pending">{stats.pending_deals}</p></div>
            <div className="bg-white rounded-xl p-3 shadow-sm"><p className="text-slate-500 text-xs">Featured</p><p className="text-xl font-bold text-purple-600" data-testid="stat-featured">{stats.featured_deals}</p></div>
            <div className="bg-white rounded-xl p-3 shadow-sm"><p className="text-slate-500 text-xs">Views</p><p className="text-xl font-bold text-blue-600" data-testid="stat-views">{stats.total_views}</p></div>
          </div>
        )}
        <div className="flex gap-2 mb-5 overflow-x-auto">
          <button onClick={() => setTab('pending')} className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap ${tab === 'pending' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`} data-testid="tab-pending">Pending ({deals.filter(d => !d.approved).length})</button>
          <button onClick={() => setTab('approved')} className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap ${tab === 'approved' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`} data-testid="tab-approved">Approved ({deals.filter(d => d.approved).length})</button>
          <button onClick={() => setTab('featured')} className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap ${tab === 'featured' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`} data-testid="tab-featured">Featured ({deals.filter(d => d.featured).length})</button>
          <button onClick={() => setTab('contacts')} className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap ${tab === 'contacts' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`} data-testid="tab-contacts">Messages ({contacts.length})</button>
          <button onClick={() => setTab('smartqr')} className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap ${tab === 'smartqr' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600'}`} data-testid="tab-smartqr">Smart QR</button>
        </div>
        
        {/* Smart QR premium payments */}
        {tab === 'smartqr' ? (
          <AdminSmartQR />
        ) : tab === 'contacts' ? (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {contacts.length > 0 ? (
              <div className="divide-y">
                {contacts.map((contact, idx) => (
                  <div key={idx} className="p-4" data-testid={`contact-${idx}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <Inbox className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{contact.name}</p>
                          <p className="text-sm text-slate-500">{contact.email}</p>
                          {contact.phone && <p className="text-sm text-slate-500">📱 {contact.phone}</p>}
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">{new Date(contact.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mb-3">{contact.message}</p>
                    <button onClick={() => openReplyForm(contact)} className="bg-orange-500 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-orange-600 transition flex items-center gap-2" data-testid={`reply-btn-${idx}`}>
                      <Mail className="w-4 h-4" /> Reply
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-10 text-center text-slate-500 text-sm">No contact messages yet</div>
            )}
          </div>
        ) : (
          /* Deals Table */
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {filteredDeals.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr><th className="text-left p-3 font-medium text-slate-600">Deal</th><th className="text-left p-3 font-medium text-slate-600">Type</th><th className="text-left p-3 font-medium text-slate-600">Area</th><th className="text-left p-3 font-medium text-slate-600">Price</th><th className="text-left p-3 font-medium text-slate-600">Actions</th></tr></thead>
                  <tbody>
                    {filteredDeals.map(deal => {
                      const offerType = getOfferType(deal.offer_type);
                      return (
                      <tr key={deal.id} className="border-t" data-testid={`deal-row-${deal.id}`}>
                        <td className="p-3"><div className="flex items-center gap-2"><div className="w-10 h-10 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">{deal.image_url ? <img src={deal.image_url} alt="" className="w-full h-full object-cover" /> : <Tag className="w-5 h-5 text-slate-400 m-auto" />}</div><div><p className="font-medium text-slate-800 text-sm">{deal.title}</p><p className="text-xs text-slate-500">{deal.category}</p></div></div></td>
                        <td className="p-3"><span className={`${offerType.color} text-xs px-2 py-1 rounded-full border`}>{offerType.emoji} {offerType.name}</span></td>
                        <td className="p-3 text-slate-600">{deal.area}</td>
                        <td className="p-3 text-slate-600">{deal.price ? `₹${deal.price}` : '-'}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {!deal.approved && <button onClick={() => approveDeal(deal.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="Approve" data-testid={`approve-btn-${deal.id}`}><Check className="w-4 h-4" /></button>}
                            <button onClick={() => openEditModal(deal)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Edit" data-testid={`edit-btn-${deal.id}`}><Edit className="w-4 h-4" /></button>
                            <button onClick={() => featureDeal(deal.id, !deal.featured)} className={`p-1.5 rounded-lg ${deal.featured ? 'text-orange-500 bg-orange-50' : 'text-slate-400 hover:bg-slate-50'}`} title="Feature" data-testid={`feature-btn-${deal.id}`}><Star className="w-4 h-4" /></button>
                            <button onClick={() => deleteDeal(deal.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Delete" data-testid={`delete-btn-${deal.id}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            ) : (<div className="p-10 text-center text-slate-500 text-sm">No deals found</div>)}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingDeal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Edit Deal</h2>
              <button onClick={() => setEditingDeal(null)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input type="text" value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={3} data-testid="edit-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Price (₹)</label>
                  <input type="number" value={editForm.price} onChange={(e) => setEditForm({...editForm, price: Number(e.target.value)})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-price" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <select value={editForm.category} onChange={(e) => setEditForm({...editForm, category: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-category">
                    {categories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Area</label>
                  <select value={editForm.area} onChange={(e) => setEditForm({...editForm, area: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-area">
                    {getAreasForCity('Pune').map(area => (<option key={area.id} value={area.name}>{area.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Offer Type</label>
                  <select value={editForm.offer_type} onChange={(e) => setEditForm({...editForm, offer_type: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-offer-type">
                    {offerTypes.map(type => (<option key={type.id} value={type.id}>{type.emoji} {type.name}</option>))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Business Name</label>
                <input type="text" value={editForm.business_name} onChange={(e) => setEditForm({...editForm, business_name: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-business-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input type="text" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Image URL</label>
                <input type="text" value={editForm.image_url} onChange={(e) => setEditForm({...editForm, image_url: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-image-url" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Maps Link</label>
                <input type="text" value={editForm.maps_link} onChange={(e) => setEditForm({...editForm, maps_link: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-maps-link" />
              </div>
            </div>
            <div className="p-4 border-t flex gap-3 justify-end">
              <button onClick={() => setEditingDeal(null)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium">Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600" data-testid="save-edit-btn">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {replyingTo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setReplyingTo(null)} data-testid="reply-modal">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Reply to {replyingTo.name}</h3>
              <button onClick={() => setReplyingTo(null)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">To</label>
                <p className="text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">{replyingTo.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Original Message</label>
                <p className="text-sm text-slate-500 bg-slate-50 px-3 py-2 rounded-lg italic">"{replyingTo.message}"</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Your Reply</label>
                <textarea 
                  value={replyMessage} 
                  onChange={(e) => setReplyMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm h-32 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Type your reply message..."
                  data-testid="reply-message"
                />
              </div>
              <p className="text-xs text-slate-400">This will open your email client with the reply pre-filled.</p>
            </div>
            <div className="p-4 border-t flex gap-3 justify-end">
              <button onClick={() => setReplyingTo(null)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium">Cancel</button>
              <button onClick={() => replyToContact(replyingTo)} className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 flex items-center gap-2" data-testid="send-reply-btn">
                <Mail className="w-4 h-4" /> Open Email Client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
